import { Preferences } from '@capacitor/preferences'

import type { CachedItemMeta } from '@/lib/exercise-media-cache'
import type { PerItemState, Phase } from '@/lib/training-types'

/**
 * On-disk store of partially-completed training sessions, keyed by
 * `exercise_id`. The training screen saves a snapshot on every meaningful
 * mutation (item advance, phase change, help press, pause, app
 * backgrounding, unmount). On entry to `/training` the view rehydrates
 * `state` from the snapshot when one exists for the requested exercise,
 * so the patient picks up where they left off.
 *
 * **Why local-only.** Unlike `lib/run-queue.ts`, this store never uploads.
 * Resumable runs are transient by definition — they become either a
 * completed `ExerciseRun` (uploaded with `status='completed'`) or are
 * eventually discarded. Today the only discard paths are the 7-day TTL
 * and `useExercisesStore().destroy(id)`. A future explicit "verwerfen"
 * UI would also call `dropSnapshot()` and upload an `abandoned` run via
 * the run queue, but that flow isn't built yet — until it lands the
 * backend stops seeing `status='abandoned'` rows.
 *
 * **TTL.** Entries older than {@link TTL_MS} are silently dropped on the
 * first load after expiry. A "weitermachen" tab from six months ago is
 * worse than no tab at all (the patient has likely forgotten the
 * context), and the underlying lingodb item content may have changed
 * upstream in the interim.
 *
 * **Storage shape.** A single Preferences key holds the whole map as
 * JSON: `Record<exercise_id, ResumableSnapshot>`. We never expect the
 * map to grow large (one entry per in-progress exercise; a patient
 * realistically has 1–3 of these at once), so a single-key blob is
 * simpler than per-exercise keys and avoids the listing API entirely.
 */

const DEFAULT_NAMESPACE = 'exercises'

/**
 * Per-namespace Preferences key. Patient-authored exercises (`exercises.id`)
 * and homework (`therapist_exercises.id`) share an autoincrement id space, so
 * each namespace gets its own blob — otherwise an own-exercise-N snapshot
 * could masquerade as homework-N (and light the wrong "weitermachen" tab).
 */
const STORAGE_KEYS: Record<string, string> = {
  exercises: 'lingotalk.exercise_runs.resumable',
  homework: 'lingotalk.homework_runs.resumable',
}
/** Default-namespace key — kept as a named handle for the test harness. */
const STORAGE_KEY = STORAGE_KEYS.exercises
function storageKeyFor(namespace: string): string {
  return STORAGE_KEYS[namespace] ?? `lingotalk.${namespace}_runs.resumable`
}

const TTL_MS = 7 * 24 * 60 * 60 * 1000

export interface ResumableSnapshot {
  exercise_id: number
  /**
   * Reused across resumes — when the patient eventually completes the
   * run, the upload's idempotency key matches the original session so a
   * pre-resume best-effort upload (none today, but possible later) won't
   * double-write.
   */
  client_run_id: string
  /** ISO of the very first mount (NOT the most recent resume). */
  session_started_at: string
  /**
   * Authoritative "active engagement" duration the patient has already
   * accumulated across all resumes. Rebased on every save so a long
   * offline gap doesn't bleed into the next `payload.duration_ms`.
   */
  session_elapsed_ms: number
  /**
   * Time already spent on `items[current_index]` across all resumes.
   * Combined with `Date.now()` at rehydration to set the in-flight
   * `started_at` such that `t_offset_ms` math on subsequent helps reads
   * correctly relative to total time on the item.
   */
  in_flight_item_elapsed_ms: number
  /**
   * Item order frozen at first mount (after any patient-settings
   * shuffle). Resume preserves this order verbatim — re-shuffling
   * mid-run would be disorienting.
   */
  items: CachedItemMeta[]
  item_states: PerItemState[]
  current_index: number
  phase: Phase
  /** ms-epoch wall clock of the most recent write — used for TTL eviction. */
  saved_at: number
}

type SnapshotMap = Record<number, ResumableSnapshot>

// --- I/O ----------------------------------------------------------------

async function readMap(key: string): Promise<SnapshotMap> {
  const { value } = await Preferences.get({ key })
  if (value === null || value === undefined) return {}
  try {
    const parsed = JSON.parse(value)
    if (parsed === null || typeof parsed !== 'object' || Array.isArray(parsed)) return {}
    const out: SnapshotMap = {}
    for (const [k, v] of Object.entries(parsed as Record<string, unknown>)) {
      const id = Number.parseInt(k, 10)
      if (Number.isNaN(id)) continue
      if (!isSnapshot(v)) continue
      out[id] = normalizePhase(v)
    }
    return out
  } catch {
    return {}
  }
}

async function writeMap(key: string, map: SnapshotMap): Promise<void> {
  await Preferences.set({ key, value: JSON.stringify(map) })
}

/**
 * `'listening'` is a transient runtime phase backed by an in-flight
 * `recognizeOnce()` promise — it has no resumable representation. If a
 * snapshot captures `phase: 'listening'` (the [phase] watcher fires the
 * moment `onRecord` flips state, and the `appStateChange` listener saves
 * during background suspension), rehydrating it leaves the mic button
 * stuck in `:disabled active` with no recognition running.
 *
 * Coerce on both read and write so existing on-disk snapshots heal
 * themselves on next load without forcing a one-shot migration sweep.
 */
function normalizePhase(s: ResumableSnapshot): ResumableSnapshot {
  if (s.phase !== 'listening') return s
  return { ...s, phase: 'idle' }
}

/**
 * Load every snapshot, dropping any past its TTL. Side-effects the on-disk
 * map if a purge happened so subsequent reads don't re-do the TTL filter.
 * Called once from the Pinia store's `install()` on app boot.
 */
export async function loadAll(namespace: string = DEFAULT_NAMESPACE): Promise<SnapshotMap> {
  const key = storageKeyFor(namespace)
  const map = await readMap(key)
  const cutoff = Date.now() - TTL_MS
  let changed = false
  const kept: SnapshotMap = {}
  for (const [k, v] of Object.entries(map)) {
    if (v.saved_at < cutoff) {
      changed = true
      continue
    }
    kept[Number.parseInt(k, 10)] = v
  }
  if (changed) {
    await writeMap(key, kept)
  }
  return kept
}

export async function loadSnapshot(
  exerciseId: number,
  namespace: string = DEFAULT_NAMESPACE,
): Promise<ResumableSnapshot | null> {
  const map = await readMap(storageKeyFor(namespace))
  const entry = map[exerciseId]
  if (entry === undefined) return null
  // Eager TTL check — relying on `loadAll()` to have purged at boot would
  // miss the case where the app stayed open past the 7-day boundary.
  if (entry.saved_at + TTL_MS < Date.now()) {
    await dropSnapshot(exerciseId, namespace)
    return null
  }
  return entry
}

export async function saveSnapshot(
  snapshot: ResumableSnapshot,
  namespace: string = DEFAULT_NAMESPACE,
): Promise<void> {
  const key = storageKeyFor(namespace)
  const map = await readMap(key)
  map[snapshot.exercise_id] = normalizePhase(snapshot)
  await writeMap(key, map)
}

export async function dropSnapshot(
  exerciseId: number,
  namespace: string = DEFAULT_NAMESPACE,
): Promise<void> {
  const key = storageKeyFor(namespace)
  const map = await readMap(key)
  if (!(exerciseId in map)) return
  delete map[exerciseId]
  await writeMap(key, map)
}

// --- helpers ------------------------------------------------------------

function isSnapshot(value: unknown): value is ResumableSnapshot {
  if (value === null || typeof value !== 'object') return false
  const v = value as Record<string, unknown>
  return (
    typeof v.exercise_id === 'number' &&
    typeof v.client_run_id === 'string' &&
    typeof v.session_started_at === 'string' &&
    typeof v.session_elapsed_ms === 'number' &&
    typeof v.in_flight_item_elapsed_ms === 'number' &&
    Array.isArray(v.items) &&
    Array.isArray(v.item_states) &&
    typeof v.current_index === 'number' &&
    typeof v.phase === 'string' &&
    typeof v.saved_at === 'number'
  )
}

// Re-exported for tests + a debug "force-expire" utility.
export const __test = { TTL_MS, STORAGE_KEY }
